import { memo, useState } from 'react';
import useSmartField from 'hooks/useSmartField';
import { useTranslate } from '@tolgee/react';
import { JustRightHeightModal } from 'components/styled/scoped';
import QuestionExample from '@knowledge/knowledges/spu/createOrEdit/formItem/QuestionExample';
import { useSpuKnowledgeMutate } from '@knowledge/knowledges/spu/crud';
import { useFormikContext } from 'formik';
import { cloneDeep, omit } from 'lodash-es';
import { message, Form, Input, Button, Modal } from 'antd';
import { DeleteOutlined } from '@ant-design/icons';
import { refreshKcList } from '@knowledge/knowledges/singleClass/connect';
import { useVerifyRepeat } from '@knowledge/knowledges/singleClass/NewKc';

const EditKcArea = memo(() => {
  const { t } = useTranslate();
  const { field } = useSmartField<string[]>('querySamples');
  const { field: field3 } = useSmartField<string>('source');
  const [visible, setVisible] = useState(false);
  if (field3.value === 'OFFICIAL') {
    return null;
  }
  return (
    <div className="flex-grow inline-flex items-center">
      {visible && <EditModal visible={visible} onCancel={() => setVisible(false)} />}
      <div className="inline-flex items-center">
        {field.value?.map((item, index) => (
          <div
            key={index}
            className="mr-[8px] bg-[#ebfaf3] text-[#00bf69] text-[12px] px-[4px] leading-[18px]"
          >
            "{item}"
          </div>
        ))}
      </div>
      <div
        onClick={() => setVisible(true)}
        className="text-[#515359] text-[12px] cursor-pointer flex-shrink-0"
      >
        {t('phrase.编辑')}
      </div>
    </div>
  );
});

function EditModal(props: { visible: boolean; onCancel: () => void }) {
  const { visible, onCancel } = props;
  const { field } = useSmartField<string[]>('querySamples');
  const { field: field2 } = useSmartField<string>('knowledgeName');
  const { field: field3 } = useSmartField<string>('source');
  const { t } = useTranslate();
  const { newUpdate, deleteOne } = useSpuKnowledgeMutate();
  const [form] = Form.useForm();
  const { values } = useFormikContext<Knowledge.SingleClass.Knowledge>();
  const verifyRepeat = useVerifyRepeat(values.id);

  return (
    <JustRightHeightModal
      destroyOnClose
      title={t('knowledge.编辑知识')}
      width={390}
      visible={visible}
      onCancel={onCancel}
      footer={
        <div className="flex items-center justify-between">
          {field3.value !== 'OFFICIAL' ? (
            <div
              className="cursor-pointer"
              onClick={() => {
                Modal.confirm({
                  title: t('single_delete_kc_title'),
                  content: t('single_delete_kc_content'),
                  onOk: () => {
                    deleteOne({
                      knowledgeId: values.id,
                    })
                      .then(() => {
                        message.success(t('phrase.删除成功'));
                        refreshKcList();
                        onCancel();
                      })
                      .catch(() => {
                        message.error(t('phrase.删除失败，请稍后再试'));
                      });
                  },
                });
              }}
            >
              <DeleteOutlined className="mr-[4px]" />
              {t('phrase.删除')}
            </div>
          ) : (
            <div />
          )}
          <div>
            <Button onClick={onCancel}>{t('phrase.取消')}</Button>
            <Button
              type="primary"
              onClick={() => {
                form.validateFields().then((formValues) => {
                  let nextValues = { ...cloneDeep(values), ...formValues };
                  nextValues = omit(nextValues, ['values']);

                  newUpdate({
                    spuKnowledge: nextValues,
                  })
                    .then(() => {
                      // field.onChange(formValues.querySamples);
                      // field2.onChange(formValues.knowledgeName);
                      refreshKcList();
                      message.success(t('operation.saveSucceed'));
                      onCancel();
                    })
                    .catch(() => {
                      message.error(t('operation.saveFailed'));
                    });
                });
              }}
            >
              {t('phrase.保存')}
            </Button>
          </div>
        </div>
      }
    >
      <Form
        form={form}
        layout="vertical"
        initialValues={{
          knowledgeName: field2.value,
          querySamples: field.value,
        }}
        validateTrigger={['onBlur']}
      >
        <Form.Item
          label={t('knowledge.knowledgeName')}
          required
          name="knowledgeName"
          rules={[
            {
              required: true,
              validateTrigger: 'onBlur',
              validator: (_, value) => {
                if (!value) {
                  return Promise.reject(
                    t('department.请输入x', { x: t('knowledge.knowledgeName') }),
                  );
                }
                if (verifyRepeat(value)) {
                  return Promise.reject(t('single_kc_repeat'));
                }
                return Promise.resolve();
              },
            },
          ]}
        >
          <Input
            disabled={field3.value === 'OFFICIAL'}
            placeholder={t('Entities.请输入')}
            maxLength={60}
            showCount
          />
        </Form.Item>
        <Form.Item label={t('knowledge.examples')} name="querySamples">
          <QuestionExample />
        </Form.Item>
      </Form>
    </JustRightHeightModal>
  );
}

EditModal.displayName = 'EditModal';

export default EditKcArea;
